import {
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  Email as EmailIcon,
  Security as PermissionsIcon,
  Person as PersonIcon,
  Pets as PetsIcon,
  Send as ResendIcon,
  Block as RevokeIcon,
  Share as ShareIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'
import React, { useCallback, useEffect, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'

import EmptyState from '../components/EmptyState'
import ToneChip from '../components/ToneChip'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import type { StatusTone } from '../themes/statusColors'
import { authHeaders } from '../utils/api'
import EditShareDialog from './EditShareDialog'
import { PERMISSION_LABELS, type VetShare } from './types'

const MySharedPets: React.FC = () => {
  const notify = useNotify()
  const translate = useTranslate()
  const [shares, setShares] = useState<VetShare[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revoking, setRevoking] = useState<string | number | null>(null)
  const [editingShare, setEditingShare] = useState<VetShare | null>(null)
  const [resending, setResending] = useState<string | number | null>(null)
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([])
  const [bulkRevoking, setBulkRevoking] = useState(false)

  const fetchShares = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await deadlineFetch('/api/vet-shares/my-shares', {
        headers: authHeaders(),
      })

      if (!response.ok) {
        throw new Error('Failed to fetch shares')
      }

      const result = await response.json()
      // Filter out revoked shares - only show active shares that can be managed
      const activeShares = (result.data || []).filter(
        (share: VetShare) => share.status !== 'revoked',
      )
      setShares(activeShares)
      setError(null)
    } catch (err: any) {
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchShares()

    // Refresh every 30 seconds
    const interval = setInterval(fetchShares, 30000)
    return () => clearInterval(interval)
  }, [fetchShares])

  // Listen for share creation events
  useEffect(() => {
    const handleShareCreated = () => {
      fetchShares()
    }

    window.addEventListener('shareCreated', handleShareCreated)

    return () => {
      window.removeEventListener('shareCreated', handleShareCreated)
    }
  }, [fetchShares])

  const revokeShare = async (shareId: string | number) => {
    try {
      setRevoking(shareId)
      const response = await deadlineFetch(`/api/vet-shares/revoke/${shareId}`, {
        method: 'POST',
        headers: authHeaders(),
      })

      if (!response.ok) {
        let errorMessage = 'Failed to revoke share'
        try {
          const errorData = await response.json()
          if (errorData.error) {
            errorMessage = errorData.error
          }
          if (errorData.details) {
            errorMessage += `: ${errorData.details}`
          }
        } catch (parseError) {
          // If we can't parse the error, fall back to status text
          errorMessage = `Failed to revoke share: ${response.status} ${response.statusText}`
        }
        throw new Error(errorMessage)
      }

      const result = await response.json()
      notify(result.message || translate('pos.shares.share_revoked_successfully'), {
        type: 'success',
      })
      fetchShares() // Refresh the list
    } catch (err: any) {
      console.error('Revoke share error:', err)
      notify(err.message || translate('pos.pet_shares.revoke_failed'), { type: 'error' })
    } finally {
      setRevoking(null)
    }
  }

  const handleRevokeShare = (shareId: string | number) => {
    if (window.confirm(translate('pos.pet_shares.revoke_confirm'))) {
      revokeShare(shareId)
    }
  }

  const handleResendInvite = async (share: VetShare) => {
    try {
      setResending(share.id)
      const response = await deadlineFetch(`/api/vet-shares/resend-invite/${share.id}`, {
        method: 'POST',
        headers: authHeaders(),
      })
      const data = await response.json()
      if (response.ok && data?.success) {
        notify('pos.pet_shares.invite_resent', { type: 'success' })
      } else {
        notify(data?.error || translate('pos.pet_shares.resend_failed'), { type: 'error' })
      }
    } catch (_e) {
      notify('pos.pet_shares.resend_failed', { type: 'error' })
    } finally {
      setResending(null)
    }
  }

  const handleCopyInviteLink = async (share: VetShare) => {
    if (!share.inviteToken) return
    const link = `${window.location.origin}/invite/accept?token=${share.inviteToken}`
    try {
      await navigator.clipboard.writeText(link)
      notify('pos.pet_shares.link_copied', { type: 'info' })
    } catch (_e) {
      notify(link, { type: 'info' }) // clipboard blocked — at least surface it
    }
  }

  const toggleSelected = (shareId: string | number) =>
    setSelectedIds((prev) =>
      prev.includes(shareId) ? prev.filter((id) => id !== shareId) : [...prev, shareId],
    )

  const handleBulkRevoke = async () => {
    if (selectedIds.length === 0) return
    if (
      !window.confirm(
        translate('pos.pet_shares.bulk_revoke_confirm', { count: selectedIds.length }),
      )
    )
      return
    try {
      setBulkRevoking(true)
      const response = await deadlineFetch('/api/vet-shares/bulk-revoke', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ shareIds: selectedIds }),
      })
      const data = await response.json()
      if (response.ok && data?.success) {
        const failedCount = data.data?.failed?.length || 0
        const revokedCount = data.data?.revoked?.length || 0
        notify(
          failedCount
            ? translate('pos.pet_shares.bulk_revoked_partial', {
                count: revokedCount,
                failed: failedCount,
              })
            : translate('pos.pet_shares.bulk_revoked', { count: revokedCount }),
          { type: failedCount ? 'warning' : 'success' },
        )
        setSelectedIds([])
        fetchShares()
      } else {
        notify(data?.error || translate('pos.pet_shares.bulk_revoke_failed'), { type: 'error' })
      }
    } catch (_e) {
      notify('pos.pet_shares.bulk_revoke_failed', { type: 'error' })
    } finally {
      setBulkRevoking(false)
    }
  }

  const getStatusChip = (share: VetShare) => {
    // Sharing is workflow, not clinical priority (UI-STYLE-GUIDE §3): live
    // shares are the quiet positive tone, everything else neutral. "Declined"
    // was error-red — the loudest color on the page for a colleague's choice.
    const statusConfig: Record<string, { tone: StatusTone; key: string; fallback: string }> = {
      active: { tone: 'positive', key: 'status_active', fallback: 'Active' },
      pending: { tone: 'neutral', key: 'status_pending', fallback: 'Pending Invitation' },
      accepted: { tone: 'positive', key: 'status_accepted', fallback: 'Accepted' },
      declined: { tone: 'neutral', key: 'status_declined', fallback: 'Declined' },
    }

    // Since revoked/expired shares are filtered out, we only need to check inviteStatus
    const config = statusConfig[share.inviteStatus] || statusConfig.active

    return (
      <ToneChip
        tone={config.tone}
        label={translate(`pos.pet_shares.${config.key}`, { _: config.fallback })}
      />
    )
  }

  const getShareTypeChip = (share: VetShare) => {
    const isAllPets = share.shareType === 'all_pets'
    const petCount = share.sharedPets?.length || 0

    return (
      <Chip
        size="small"
        icon={<PetsIcon />}
        label={
          isAllPets
            ? translate('pos.shared_with_me.all_pets', { _: 'All Pets' })
            : translate('pos.pet_shares.chip_n_pets', {
                smart_count: petCount,
                _: `${petCount} Pets`,
              })
        }
        variant="outlined"
        color={isAllPets ? 'primary' : 'secondary'}
      />
    )
  }

  const getPermissionSummary = (permissions: string[]) =>
    permissions
      .map((p) =>
        translate(`pos.shared_with_me.permissions.${p}`, { _: PERMISSION_LABELS[p] || p }),
      )
      .join(', ')

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {translate('pos.pet_shares.load_failed')}
      </Alert>
    )
  }

  if (shares.length === 0) {
    return (
      <EmptyState
        icon={<ShareIcon />}
        title={translate('pos.pet_shares.empty_title')}
        description={translate('pos.pet_shares.empty_subtitle')}
      />
    )
  }

  return (
    <Box sx={{ mt: 2 }}>
      {selectedIds.length > 0 && (
        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="body2">
            {translate('pos.pet_shares.selected_count', { count: selectedIds.length })}
          </Typography>
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<RevokeIcon />}
            onClick={handleBulkRevoke}
            disabled={bulkRevoking}
          >
            {translate('pos.pet_shares.revoke_selected')}
          </Button>
        </Box>
      )}
      <Stack spacing={2}>
        {shares.map((share: VetShare) => (
          <Card key={share.id} variant="outlined">
            <CardContent>
              <Box display="flex" justifyContent="between" alignItems="flex-start" mb={2}>
                <Checkbox
                  size="small"
                  checked={selectedIds.includes(share.id)}
                  onChange={() => toggleSelected(share.id)}
                  inputProps={{ 'aria-label': `select share ${share.id}` }}
                  sx={{ mt: 0.5, mr: 1 }}
                />
                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {share.inviteStatus === 'pending' ? (
                      <Avatar sx={{ bgcolor: 'warning.main' }}>
                        <EmailIcon />
                      </Avatar>
                    ) : (
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        <PersonIcon />
                      </Avatar>
                    )}
                    <Box>
                      <Typography variant="h6">
                        {share.recipient?.name || share.recipientEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {share.recipient?.email || share.recipientEmail}
                      </Typography>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    {getStatusChip(share)}
                    {getShareTypeChip(share)}
                  </Box>

                  <Box mb={1}>
                    <Typography variant="body2" color="text.secondary">
                      <PermissionsIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {translate('pos.pet_shares.permissions_summary', {
                        list: getPermissionSummary(share.permissions),
                        _: `Permissions: ${getPermissionSummary(share.permissions)}`,
                      })}
                    </Typography>
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    <TimeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    {translate('pos.pet_shares.created_label', {
                      date: format(new Date(share.createdAt), 'MMM dd, yyyy HH:mm'),
                      _: `Created: ${format(new Date(share.createdAt), 'MMM dd, yyyy HH:mm')}`,
                    })}
                  </Typography>

                  {share.expiresAt && (
                    <Typography variant="body2" color="text.secondary">
                      {translate('pos.shared_with_me.expires_at', {
                        date: format(new Date(share.expiresAt), 'MMM dd, yyyy'),
                        _: `Expires: ${format(new Date(share.expiresAt), 'MMM dd, yyyy')}`,
                      })}
                    </Typography>
                  )}

                  {share.note && (
                    <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                      "{share.note}"
                    </Typography>
                  )}

                  {share.shareType === 'specific_pets' && share.sharedPets && (
                    <Box mt={1}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {translate('pos.shared_with_me.shared_pets_label')}
                      </Typography>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {share.sharedPets.map((pet) => (
                          <Chip
                            key={pet.id}
                            label={pet.name || translate('pos.common.pet_fallback', { id: pet.id })}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Box display="flex" flexDirection="column">
                  <Tooltip title={translate('pos.pet_shares.edit_title')}>
                    <IconButton
                      color="primary"
                      onClick={() => setEditingShare(share)}
                      aria-label={translate('pos.pet_shares.aria.edit', { id: share.id })}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  {share.inviteStatus === 'pending' && (
                    <>
                      <Tooltip title={translate('pos.pet_shares.resend_invite')}>
                        <IconButton
                          color="primary"
                          onClick={() => handleResendInvite(share)}
                          disabled={resending === share.id}
                          aria-label={translate('pos.pet_shares.aria.resend', { id: share.id })}
                        >
                          <ResendIcon />
                        </IconButton>
                      </Tooltip>
                      {share.inviteToken && (
                        <Tooltip title={translate('pos.pet_shares.copy_link')}>
                          <IconButton
                            onClick={() => handleCopyInviteLink(share)}
                            aria-label={translate('pos.pet_shares.aria.copy', { id: share.id })}
                          >
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  )}
                  <Tooltip title={translate('pos.pet_shares.revoke_share')}>
                    <IconButton
                      color="error"
                      onClick={() => handleRevokeShare(share.id)}
                      disabled={revoking === share.id}
                      aria-label={translate('pos.pet_shares.aria.revoke', { id: share.id })}
                    >
                      <RevokeIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
      {editingShare && (
        <EditShareDialog
          share={editingShare}
          open
          onClose={() => setEditingShare(null)}
          onSaved={fetchShares}
        />
      )}
    </Box>
  )
}

export default MySharedPets
